import React, { useEffect,useState } from 'react';
import {
    List,
    Datagrid,
    TextField,
    NumberField ,
    EmailField,
    EditButton,
    DeleteButton,
    TextInput,
    FilterButton,
    CreateButton,
    ExportButton,
    TopToolbar,
    Edit,
    SimpleForm,
    SelectInput,
    Show, 
    SimpleShowLayout,
    TabbedShowLayout,
    Create,
    useShowController,
    useGetOne,
    DateField,
    useRecordContext,
    DateInput ,
    useDelete,
    useNotify,
    useRefresh,
    Confirm,
    BulkDeleteButton,
    useRedirect,
} from 'react-admin';
import { useParams, useNavigate} from 'react-router-dom';
import { CustomerAvatar } from "./CustomerAvatar";
import { 
    Box, 
    Typography, 
    Divider, 
    Card, 
    CardContent, 
    CardMedia, 
    Grid, 
    Chip, 
    IconButton, 
    Dialog, 
    DialogContent, 
    DialogActions, 
    Button,
    Tooltip,
    DialogTitle,
} from '@mui/material';
import { 
    Delete as DeleteIcon,
    Download as DownloadIcon, 
    ZoomIn as ZoomInIcon, 
    Close as CloseIcon, 
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';

import { Link } from 'react-router-dom';
import { Person as PersonIcon } from '@mui/icons-material';
import OculusSubscriptions from './OculusSubscriptions';
import AITab from './AITab';

const BackButton = () => {
    const redirect = useRedirect();

    const handleBack = () => {
        redirect('/users');
    };

    return (
        <Button
            onClick={handleBack}
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={{ 
                mr: 2,
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                    backgroundColor: 'primary.light',
                    borderColor: 'primary.dark'
                }
            }}
        >
            Volver a Lista
        </Button>
    );
};

// Componente para eliminar desde la vista Show
const DeleteFromShowButton = () => {
    const record = useRecordContext();
    const [open, setOpen] = useState(false);
    const [deleteOne, { isLoading }] = useDelete();
    const notify = useNotify();
    const navigate = useNavigate();

    const handleDelete = async () => {
        try {
            await deleteOne('users', { 
                id: record.id,
                previousData: record 
            });
            
            notify(`Usuario ${record.username || record.email} eliminado exitosamente`, { 
                type: 'success' 
            });
            
            // Redirigir a la lista después de eliminar
            navigate('/users');
        } catch (error) {
            notify(`Error al eliminar usuario: ${error.message}`, { 
                type: 'error' 
            });
        }
        setOpen(false);
    };

    return (
        <>
            <Button
                onClick={() => setOpen(true)}
                disabled={isLoading}
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                sx={{ ml: 2 }}
            >
                {isLoading ? 'Eliminando...' : 'Eliminar Usuario'}
            </Button>
            
            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>Confirmar eliminación</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        ¿Estás seguro de que quieres eliminar este usuario?
                    </Typography>
                    <Box sx={{ 
                        p: 2, 
                        backgroundColor: '#ffebee', 
                        borderRadius: 1,
                        border: '1px solid #f44336'
                    }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            Usuario: {record?.username || 'Sin nombre'}
                        </Typography>
                        <Typography variant="body2">
                            Email: {record?.email}
                        </Typography>
                        <Typography variant="body2">
                            ID: {record?.id}
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ 
                        mt: 2, 
                        color: 'error.main',
                        fontWeight: 'bold'
                    }}>
                        ⚠️ Esta acción eliminará permanentemente:
                    </Typography>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                        <li>Todos los datos del usuario</li>
                        <li>Historial de licencias</li>
                        <li>Registros de actividad</li>
                        <li>Proyectos asociados</li>
                        <li>Autorizaciones</li>
                    </ul>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>
                        Cancelar
                    </Button>
                    <Button 
                        onClick={handleDelete}
                        disabled={isLoading}
                        variant="contained"
                        color="error"
                    >
                        {isLoading ? 'Eliminando...' : 'Sí, eliminar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};


// Componente para eliminación masiva personalizada
const CustomBulkDeleteButton = () => {
    const [deleteMany, { isLoading }] = useDelete();
    const notify = useNotify();
    const refresh = useRefresh();

    const handleBulkDelete = async (selectedIds) => {
        try {
            await Promise.all(
                selectedIds.map(id => 
                    deleteMany('users', { id })
                )
            );
            
            notify(`${selectedIds.length} usuarios eliminados exitosamente`, { 
                type: 'success' 
            });
            refresh();
        } catch (error) {
            notify(`Error al eliminar usuarios: ${error.message}`, { 
                type: 'error' 
            });
        }
    };

    return (
        <BulkDeleteButton
            mutationMode="pessimistic"
            confirmTitle="Eliminar usuarios seleccionados"
            confirmContent="¿Estás seguro de que quieres eliminar todos los usuarios seleccionados? Esta acción no se puede deshacer."
            onSuccess={handleBulkDelete}
        />
    );
};


// Componente personalizado para eliminación con confirmación mejorada
// src/Pages/Users/index.js

// ✅ Componente CustomDeleteButton corregido
const CustomDeleteButton = () => {
    const record = useRecordContext();
    const [open, setOpen] = useState(false);
    const [deleteOne, { isLoading }] = useDelete();
    const notify = useNotify();
    const refresh = useRefresh();

    // ✅ Prevenir propagación del evento
    const handleClick = (e) => {
        e.stopPropagation(); // Evita que el click se propague al row
        e.preventDefault();  // Previene comportamiento por defecto
        setOpen(true);
    };

    const handleDialogClose = (e) => {
        if (e) {
            e.stopPropagation();
        }
        setOpen(false);
    };

    const handleConfirm = async (e) => {
        if (e) {
            e.stopPropagation();
        }
        
        try {
            await deleteOne('users', { 
                id: record.id,
                previousData: record 
            });
            
            notify(`Usuario ${record.username || record.email} eliminado exitosamente`, { 
                type: 'success' 
            });
            refresh();
            setOpen(false);
        } catch (error) {
            notify(`Error al eliminar usuario: ${error.message}`, { 
                type: 'error' 
            });
            setOpen(false);
        }
    };

    return (
        <>
            <IconButton
                onClick={handleClick} // ✅ Usar handleClick con stopPropagation
                disabled={isLoading}
                size="small"
                sx={{ 
                    color: 'error.main',
                    '&:hover': {
                        backgroundColor: 'error.light',
                        color: 'white'
                    }
                }}
            >
                {isLoading ? '⏳' : '🗑️'}
            </IconButton>
            
            <Confirm
                isOpen={open}
                loading={isLoading}
                title="Confirmar eliminación"
                content={
                    <Box>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            ¿Estás seguro de que quieres eliminar este usuario?
                        </Typography>
                        <Box sx={{ 
                            p: 2, 
                            backgroundColor: '#fff3cd', 
                            borderRadius: 1,
                            border: '1px solid #ffeaa7'
                        }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                Usuario: {record.username || 'Sin nombre'}
                            </Typography>
                            <Typography variant="body2">
                                Email: {record.email}
                            </Typography>
                            <Typography variant="body2">
                                ID: {record.id}
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ 
                            mt: 2, 
                            color: 'error.main',
                            fontWeight: 'bold'
                        }}>
                            ⚠️ Esta acción no se puede deshacer
                        </Typography>
                        <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                            <li>Todos los datos del usuario</li>
                            <li>Historial de licencias</li>
                            <li>Registros de actividad</li>
                            <li>Proyectos asociados</li>
                            <li>Autorizaciones</li>
                        </ul>
                    </Box>
                }
                onConfirm={handleConfirm}
                onClose={handleDialogClose}
                confirm="Sí, eliminar"
                cancel="Cancelar"
            />
        </>
    );
};

// Componente para mostrar el historial de cambios de licencias
const LicenseHistoryTab = () => {
    const record = useRecordContext();
    const [selectedFilter, setSelectedFilter] = useState('all');

    if (!record || !record.licencias || record.licencias.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                    📜 Historial de Cambios de Licencias
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Este usuario no tiene historial de cambios de licencias.
                </Typography>
            </Box>
        );
    }

    const getLicenseTypeColor = (licenseType) => {
        const colors = {
            'trial': { color: '#388e3c', backgroundColor: '#e8f5e8', borderColor: '#4caf50' },
            'premium': { color: '#7b1fa2', backgroundColor: '#f3e5f5', borderColor: '#9c27b0' },
            'basic': { color: '#1976d2', backgroundColor: '#e3f2fd', borderColor: '#2196f3' },
            'enterprise': { color: '#d84315', backgroundColor: '#ffebee', borderColor: '#ff5722' },
            'free': { color: '#616161', backgroundColor: '#f5f5f5', borderColor: '#9e9e9e' }
        };
        const key = licenseType?.toLowerCase() || 'free';
        return colors[key] || colors['free'];
    };

    const getLicenseIcon = (licenseType) => {
        const icons = {
            'trial': '🆓',
            'premium': '⭐',
            'basic': '📦',
            'enterprise': '🏢',
            'free': '🔓'
        };
        return icons[licenseType?.toLowerCase()] || '📄';
    };

    const getChangeTypeIcon = (anterior, nuevo) => {
        const licenseHierarchy = {
            'free': 0,
            'trial': 1,
            'basic': 2,
            'premium': 3,
            'enterprise': 4
        };
        
        const anteriorLevel = licenseHierarchy[anterior?.toLowerCase()] || 0;
        const nuevoLevel = licenseHierarchy[nuevo?.toLowerCase()] || 0;
        
        if (nuevoLevel > anteriorLevel) {
            return { icon: '⬆️', type: 'upgrade', color: '#2e7d32' };
        } else if (nuevoLevel < anteriorLevel) {
            return { icon: '⬇️', type: 'downgrade', color: '#d32f2f' };
        } else {
            return { icon: '🔄', type: 'change', color: '#f57c00' };
        }
    };

    const formatPrice = (price) => {
        if (!price || price === '0' || price === 0) return 'Gratis';
        return `$${parseFloat(price).toFixed(2)}`;
    };

    // Filtrar y ordenar licencias
    const getFilteredLicenses = () => {
        let filteredLicenses = [...record.licencias];
        
        if (selectedFilter !== 'all') {
            const changeTypes = filteredLicenses.map(license => 
                getChangeTypeIcon(license.tipotrial_anterior, license.tipotrial_nuevo).type
            );
            filteredLicenses = filteredLicenses.filter((license, index) => 
                changeTypes[index] === selectedFilter
            );
        }
        
        // Ordenar por fecha (más recientes primero)
        return filteredLicenses.sort((a, b) => new Date(b.fecha_cambio) - new Date(a.fecha_cambio));
    };

    const filteredLicenses = getFilteredLicenses();

    // Calcular estadísticas
    const getStats = () => {
        const stats = {
            total: record.licencias.length,
            upgrades: 0,
            downgrades: 0,
            changes: 0
        };

        record.licencias.forEach(license => {
            const changeType = getChangeTypeIcon(license.tipotrial_anterior, license.tipotrial_nuevo).type;
            stats[changeType + 's']++;
        });

        return stats;
    };

    const stats = getStats();

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, color: 'primary.main' }}>
                📜 Historial de Cambios de Licencias ({record.licencias.length})
            </Typography>

            {/* Filtros */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                    🔍 Filtrar por tipo de cambio:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                        label={`Todos (${stats.total})`}
                        onClick={() => setSelectedFilter('all')}
                        variant={selectedFilter === 'all' ? 'filled' : 'outlined'}
                        sx={{
                            backgroundColor: selectedFilter === 'all' ? 'primary.main' : 'transparent',
                            color: selectedFilter === 'all' ? 'white' : 'text.primary',
                            '&:hover': {
                                backgroundColor: selectedFilter === 'all' ? 'primary.dark' : 'action.hover'
                            }
                        }}
                    />
                    <Chip
                        label={`⬆️ Mejoras (${stats.upgrades})`}
                        onClick={() => setSelectedFilter('upgrade')}
                        variant={selectedFilter === 'upgrade' ? 'filled' : 'outlined'}
                        sx={{
                            backgroundColor: selectedFilter === 'upgrade' ? 'success.main' : 'transparent',
                            color: selectedFilter === 'upgrade' ? 'white' : 'text.primary',
                            '&:hover': {
                                backgroundColor: selectedFilter === 'upgrade' ? 'success.dark' : 'action.hover'
                            }
                        }}
                    />
                    <Chip
                        label={`⬇️ Reducciones (${stats.downgrades})`}
                        onClick={() => setSelectedFilter('downgrade')}
                        variant={selectedFilter === 'downgrade' ? 'filled' : 'outlined'}
                        sx={{
                            backgroundColor: selectedFilter === 'downgrade' ? 'error.main' : 'transparent',
                            color: selectedFilter === 'downgrade' ? 'white' : 'text.primary',
                            '&:hover': {
                                backgroundColor: selectedFilter === 'downgrade' ? 'error.dark' : 'action.hover'
                            }
                        }}
                    />
                    <Chip
                        label={`🔄 Cambios (${stats.changes})`}
                        onClick={() => setSelectedFilter('change')}
                        variant={selectedFilter === 'change' ? 'filled' : 'outlined'}
                        sx={{
                            backgroundColor: selectedFilter === 'change' ? 'warning.main' : 'transparent',
                            color: selectedFilter === 'change' ? 'white' : 'text.primary',
                            '&:hover': {
                                backgroundColor: selectedFilter === 'change' ? 'warning.dark' : 'action.hover'
                            }
                        }}
                    />
                </Box>
            </Box>

            {/* Resumen estadístico */}
            <Box sx={{ 
                mb: 4, 
                p: 3, 
                backgroundColor: '#f8f9fa', 
                borderRadius: 2,
                border: '1px solid #e0e0e0'
            }}>
                <Typography variant="h6" sx={{ 
                    mb: 2, 
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    📊 Resumen del Historial {selectedFilter !== 'all' && `(Filtrado: ${selectedFilter})`}
                </Typography>
                
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ 
                                color: 'primary.main', 
                                fontWeight: 'bold' 
                            }}>
                                {filteredLicenses.length}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {selectedFilter === 'all' ? 'Total Cambios' : 'Cambios Filtrados'}
                            </Typography>
                        </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ 
                                color: 'success.main', 
                                fontWeight: 'bold' 
                            }}>
                                {stats.upgrades}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Mejoras
                            </Typography>
                        </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ 
                                color: 'error.main', 
                                fontWeight: 'bold' 
                            }}>
                                {stats.downgrades}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Reducciones
                            </Typography>
                        </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ 
                                color: 'warning.main', 
                                fontWeight: 'bold' 
                            }}>
                                {stats.changes}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Otros Cambios
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* Lista de cambios de licencias */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {filteredLicenses.length === 0 ? (
                    <Box sx={{ textAlign: 'center', p: 4 }}>
                        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                            🔍 No se encontraron cambios
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            No hay cambios de licencias que coincidan con el filtro seleccionado: <strong>{selectedFilter}</strong>
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={() => setSelectedFilter('all')}
                            sx={{ mt: 2 }}
                        >
                            Ver todos los cambios
                        </Button>
                    </Box>
                ) : (
                    filteredLicenses.map((license, index) => {
                        const changeInfo = getChangeTypeIcon(license.tipotrial_anterior, license.tipotrial_nuevo);
                        
                        return (
                            <Card key={index} sx={{ 
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.12)'
                                }
                            }}>
                                {/* Header del cambio */}
                                <Box sx={{ 
                                    p: 2, 
                                    backgroundColor: '#f8f9fa',
                                    borderBottom: '1px solid #e0e0e0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2
                                }}>
                                    <Box sx={{
                                        width: 50,
                                        height: 50,
                                        borderRadius: '50%',
                                        backgroundColor: changeInfo.color,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '1.5rem'
                                    }}>
                                        {changeInfo.icon}
                                    </Box>
                                    
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h6" sx={{ 
                                            fontSize: '1rem',
                                            fontWeight: 'bold',
                                            color: 'text.primary',
                                            mb: 0.5
                                        }}>
                                            Cambio de Licencia
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {new Date(license.fecha_cambio).toLocaleString('es-ES')}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Contenido del cambio */}
                                <CardContent sx={{ p: 3 }}>
                                    <Grid container spacing={3}>
                                        {/* Licencia anterior */}
                                        <Grid item xs={12} md={5}>
                                            <Typography variant="subtitle2" sx={{ 
                                                mb: 2, 
                                                fontWeight: 'bold',
                                                color: 'text.secondary',
                                                textAlign: 'center'
                                            }}>
                                                📋 Licencia Anterior
                                            </Typography>
                                            <Box sx={{ 
                                                p: 2, 
                                                border: '2px solid #e0e0e0',
                                                borderRadius: 2,
                                                textAlign: 'center',
                                                backgroundColor: '#fafafa'
                                            }}>
                                                <Box sx={{ mb: 2 }}>
                                                    <Chip
                                                        label={`${getLicenseIcon(license.tipotrial_anterior)} ${license.tipotrial_anterior || 'No especificada'}`}
                                                        sx={{
                                                            ...getLicenseTypeColor(license.tipotrial_anterior),
                                                            fontWeight: 'bold',
                                                            fontSize: '0.875rem',
                                                            mb: 1
                                                        }}
                                                    />
                                                </Box>
                                                <Typography variant="h6" sx={{ 
                                                    color: 'text.primary',
                                                    fontWeight: 'bold'
                                                }}>
                                                    {formatPrice(license.currentprice_anterior)}
                                                </Typography>
                                            </Box>
                                        </Grid>

                                        {/* Flecha de cambio */}
                                        <Grid item xs={12} md={2}>
                                            <Box sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center',
                                                height: '100%',
                                                minHeight: '120px'
                                            }}>
                                                <Box sx={{
                                                    fontSize: '2rem',
                                                    color: changeInfo.color,
                                                    fontWeight: 'bold'
                                                }}>
                                                    {changeInfo.icon}
                                                </Box>
                                            </Box>
                                        </Grid>

                                        {/* Licencia nueva */}
                                        <Grid item xs={12} md={5}>
                                            <Typography variant="subtitle2" sx={{ 
                                                mb: 2, 
                                                fontWeight: 'bold',
                                                color: 'text.secondary',
                                                textAlign: 'center'
                                            }}>
                                                ✨ Licencia Nueva
                                            </Typography>
                                            <Box sx={{ 
                                                p: 2, 
                                                border: '2px solid',
                                                borderColor: changeInfo.color,
                                                borderRadius: 2,
                                                textAlign: 'center',
                                                backgroundColor: 'white'
                                            }}>
                                                <Box sx={{ mb: 2 }}>
                                                    <Chip
                                                        label={`${getLicenseIcon(license.tipotrial_nuevo)} ${license.tipotrial_nuevo || 'No especificada'}`}
                                                        sx={{
                                                            ...getLicenseTypeColor(license.tipotrial_nuevo),
                                                            fontWeight: 'bold',
                                                            fontSize: '0.875rem',
                                                            mb: 1
                                                        }}
                                                    />
                                                </Box>
                                                <Typography variant="h6" sx={{ 
                                                    color: 'text.primary',
                                                    fontWeight: 'bold'
                                                }}>
                                                    {formatPrice(license.currentprice_nuevo)}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    {/* Información adicional del cambio */}
                                    <Box sx={{ 
                                        mt: 3, 
                                        pt: 2, 
                                        borderTop: '1px solid #e0e0e0',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                        gap: 2
                                    }}>
                                        <Box>
                                            <Typography variant="body2" sx={{ 
                                                color: 'text.secondary',
                                                fontWeight: 'bold'
                                            }}>
                                                Diferencia de precio:
                                            </Typography>
                                            <Typography variant="body1" sx={{ 
                                                color: changeInfo.color,
                                                fontWeight: 'bold'
                                            }}>
                                                {(() => {
                                                    const anterior = parseFloat(license.currentprice_anterior) || 0;
                                                    const nuevo = parseFloat(license.currentprice_nuevo) || 0;
                                                    const diferencia = nuevo - anterior;
                                                    
                                                    if (diferencia > 0) {
                                                        return `+$${diferencia.toFixed(2)}`;
                                                    } else if (diferencia < 0) {
                                                        return `-$${Math.abs(diferencia).toFixed(2)}`;
                                                    } else {
                                                        return 'Sin cambio';
                                                    }
                                                })()}
                                            </Typography>
                                        </Box>
                                        
                                        <Box sx={{ textAlign: 'right' }}>
                                            <Typography variant="caption" sx={{ 
                                                color: 'text.secondary',
                                                fontStyle: 'italic'
                                            }}>
                                                Cambio #{filteredLicenses.length - index}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        );
                    })
                )}
            </Box>
        </Box>
    );
};

// Componente personalizado para calcular la duración de la suscripción
const SubscriptionDurationField = (props) => {
    const record = useRecordContext();
    
    if (!record) {
        return <span>Sin datos</span>;
    }
    
    // Verificar si hay suscripciones de Oculus canceladas
    const oculusCancelledSubs = record.oculus?.filter(sub => sub.cancellation_time) || [];
    
    if (!record.subscriptionstarttime || !record.subscriptionendtime) {
        return <span>Fechas no disponibles</span>;
    }
    
    try {
        // Crear objetos Date desde strings ISO
        const startDate = new Date(record.subscriptionstarttime);
        const endDate = new Date(record.subscriptionendtime);
        const currentDate = new Date();
        
        // Verificar cancelación en el array de oculus
        const oculusCancellation = record.oculus?.find(sub => sub.cancellation_time);
        const cancellationDate = oculusCancellation ? new Date(oculusCancellation.cancellation_time) : null;
        
        // Verificar si las fechas son válidas
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
            return <span>Fechas inválidas</span>;
        }
        
        // Verificar si la fecha de cancelación es válida
        const isCancelled = cancellationDate && !isNaN(cancellationDate.getTime());
        
        // Calcular diferencia total de la suscripción
        const totalDiffTime = endDate - startDate;
        const totalDays = Math.ceil(totalDiffTime / (1000 * 60 * 60 * 24));
        
        // Calcular días restantes
        const remainingTime = endDate - currentDate;
        const remainingDays = Math.ceil(remainingTime / (1000 * 60 * 60 * 24));
        
        // Formatear duración total
        let totalDuration;
        if (totalDays >= 365) {
            const years = Math.floor(totalDays / 365);
            const days = totalDays % 365;
            totalDuration = `${years} año${years > 1 ? 's' : ''}${days > 0 ? ` y ${days} día${days > 1 ? 's' : ''}` : ''}`;
        } else if (totalDays >= 30) {
            const months = Math.floor(totalDays / 30);
            const days = totalDays % 30;
            totalDuration = `${months} mes${months > 1 ? 'es' : ''}${days > 0 ? ` y ${days} día${days > 1 ? 's' : ''}` : ''}`;
        } else {
            totalDuration = `${totalDays} día${totalDays > 1 ? 's' : ''}`;
        }
        
        // Determinar estado y color considerando la cancelación
        let status;
        let color;
        let backgroundColor;
        let borderColor;
        let icon;
        
        if (isCancelled) {
            // Suscripción cancelada
            if (remainingDays > 0) {
                // Cancelada pero aún activa
                status = `Cancelada - Activa hasta ${endDate.toLocaleDateString('es-ES')}`;
                color = '#f57c00'; // Naranja
                backgroundColor = '#fff3e0'; // Naranja claro
                borderColor = '#ff9800';
                icon = '⚠️';
            } else {
                // Cancelada y expirada
                status = `Cancelada y Expirada`;
                color = '#c62828'; // Rojo intenso
                backgroundColor = '#ffebee'; // Rosa claro
                borderColor = '#f44336';
                icon = '❌';
            }
        } else {
            // Suscripción no cancelada
            if (remainingDays > 0) {
                status = `Activa (${remainingDays} días restantes)`;
                color = '#2e7d32'; // Verde oscuro
                backgroundColor = '#e8f5e8'; // Verde claro
                borderColor = '#4caf50';
                icon = '✅';
            } else if (remainingDays === 0) {
                status = "Expira hoy";
                color = '#d84315'; // Rojo menos intenso
                backgroundColor = '#ffebee'; // Rosa claro
                borderColor = '#ff5722';
                icon = '⏰';
            } else {
                status = `Expirada (${Math.abs(remainingDays)} días)`;
                color = '#c62828'; // Rojo intenso
                backgroundColor = '#ffebee'; // Rosa claro
                borderColor = '#f44336';
                icon = '❌';
            }
        }
        
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {/* Badge principal con estado */}
                <Box
                    sx={{ 
                        fontSize: '0.875rem',
                        color: color,
                        backgroundColor: backgroundColor,
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: `1px solid ${borderColor}`,
                        fontWeight: '500',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        width: 'fit-content',
                        minWidth: 'auto',
                        maxWidth: 'none',
                        whiteSpace: 'nowrap',
                        textAlign: 'center'
                    }}
                >
                    <span>{icon}</span>
                    <span>{status}</span>
                </Box>
                
                {/* Información adicional de cancelación */}
                {isCancelled && (
                    <Box
                        sx={{
                            fontSize: '0.75rem',
                            color: '#666',
                            backgroundColor: '#f5f5f5',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            border: '1px solid #ddd',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            width: 'fit-content',
                            fontStyle: 'italic'
                        }}
                    >
                        <span>🗓️</span>
                        <span>Cancelada el {cancellationDate.toLocaleDateString('es-ES')}</span>
                    </Box>
                )}
                
                {/* Información de suscripciones Oculus canceladas */}
                {oculusCancelledSubs.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" sx={{ 
                            color: '#d32f2f', 
                            fontWeight: 'bold',
                            display: 'block',
                            mb: 0.5
                        }}>
                            🎮 Suscripciones Oculus Canceladas:
                        </Typography>
                        {oculusCancelledSubs.map((sub, index) => (
                            <Box
                                key={sub.subscription_id || index}
                                sx={{
                                    fontSize: '0.75rem',
                                    color: '#d32f2f',
                                    backgroundColor: '#ffebee',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    border: '1px solid #f44336',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    width: 'fit-content',
                                    mb: 0.5,
                                    mr: 0.5
                                }}
                            >
                                <span>❌</span>
                                <span>
                                    SKU: {sub.sku || 'N/A'} - 
                                    Cancelada: {new Date(sub.cancellation_time).toLocaleDateString('es-ES')}
                                    {sub.period_end_time && ` - Activa hasta: ${new Date(sub.period_end_time).toLocaleDateString('es-ES')}`}
                                </span>
                            </Box>
                        ))}
                    </Box>
                )}
                
                {/* Duración total (más pequeña) */}
                <Box
                    sx={{
                        fontSize: '0.75rem',
                        color: '#888',
                        fontWeight: 'normal',
                        marginTop: 0.5
                    }}
                >
                    Duración: {totalDuration}
                </Box>
            </Box>
        );
        
    } catch (error) {
        console.error('Error calculando duración:', error);
        return <span>Error de cálculo</span>;
    }
};


const LogsTab = () => {
    const record = useRecordContext();
    const [expandedLog, setExpandedLog] = useState(null);
    const [selectedFilter, setSelectedFilter] = useState('all'); // ✅ Nuevo estado para filtros

    if (!record || !record.logs || record.logs.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                    📋 Registros de Actividad
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Este usuario no tiene registros de actividad.
                </Typography>
            </Box>
        );
    }

    const getStatusColor = (status) => {
        if (status >= 200 && status < 300) {
            return { color: '#2e7d32', backgroundColor: '#e8f5e8', borderColor: '#4caf50' }; // Verde - Éxito
        } else if (status >= 300 && status < 400) {
            return { color: '#f57c00', backgroundColor: '#fff3e0', borderColor: '#ff9800' }; // Naranja - Redirección
        } else if (status >= 400 && status < 500) {
            return { color: '#d32f2f', backgroundColor: '#ffebee', borderColor: '#f44336' }; // Rojo - Error cliente
        } else if (status >= 500) {
            return { color: '#7b1fa2', backgroundColor: '#f3e5f5', borderColor: '#9c27b0' }; // Púrpura - Error servidor
        } else {
            return { color: '#616161', backgroundColor: '#f5f5f5', borderColor: '#9e9e9e' }; // Gris - Desconocido
        }
    };

    const getSourceIcon = (source) => {
        const icons = {
            'GetProyects': '📁',
            'Login': '🔐',
            'Upload': '📤',
            'Download': '📥',
            'Delete': '🗑️',
            'Update': '✏️',
            'Create': '➕',
            'View': '👁️'
        };
        return icons[source] || '📋';
    };

    const formatJsonBody = (bodyString) => {
        try {
            const parsed = JSON.parse(bodyString);
            return JSON.stringify(parsed, null, 2);
        } catch (error) {
            return bodyString;
        }
    };

    const handleToggleExpand = (logId) => {
        setExpandedLog(expandedLog === logId ? null : logId);
    };

    // ✅ Función para manejar filtros
    const handleFilterClick = (filterType) => {
        setSelectedFilter(selectedFilter === filterType ? 'all' : filterType);
    };

    // ✅ Obtener tipos únicos de fuentes para los filtros
    const uniqueSources = [...new Set(record.logs.map(log => log.source))];
    const statusCategories = [
        { key: 'success', label: 'Exitosos', range: [200, 299], icon: '✅' },
        { key: 'redirect', label: 'Redirecciones', range: [300, 399], icon: '🔄' },
        { key: 'client_error', label: 'Errores Cliente', range: [400, 499], icon: '❌' },
        { key: 'server_error', label: 'Errores Servidor', range: [500, 599], icon: '🚨' }
    ];

    // ✅ Filtrar logs según el filtro seleccionado
    const getFilteredLogs = () => {
        let filteredLogs = [...record.logs];

        if (selectedFilter !== 'all') {
            // Filtrar por fuente
            if (uniqueSources.includes(selectedFilter)) {
                filteredLogs = filteredLogs.filter(log => log.source === selectedFilter);
            }
            // Filtrar por categoría de status
            else {
                const statusCategory = statusCategories.find(cat => cat.key === selectedFilter);
                if (statusCategory) {
                    filteredLogs = filteredLogs.filter(log => 
                        log.status >= statusCategory.range[0] && log.status <= statusCategory.range[1]
                    );
                }
            }
        }

        // Ordenar por fecha (más recientes primero)
        return filteredLogs.sort((a, b) => new Date(b.date) - new Date(a.date));
    };

    const filteredLogs = getFilteredLogs();

    // ✅ Calcular estadísticas para los filtros
    const getFilterStats = () => {
        const stats = {
            all: record.logs.length,
            success: record.logs.filter(log => log.status >= 200 && log.status < 300).length,
            redirect: record.logs.filter(log => log.status >= 300 && log.status < 400).length,
            client_error: record.logs.filter(log => log.status >= 400 && log.status < 500).length,
            server_error: record.logs.filter(log => log.status >= 500).length
        };

        // Agregar estadísticas por fuente
        uniqueSources.forEach(source => {
            stats[source] = record.logs.filter(log => log.source === source).length;
        });

        return stats;
    };

    const filterStats = getFilterStats();

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, color: 'primary.main' }}>
                📋 Registros de Actividad ({record.logs.length})
            </Typography>
            
            {/* ✅ Filtros interactivos */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                    🔍 Filtrar por:
                </Typography>
                
                {/* Filtros por estado */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                        Estado de Respuesta:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Chip
                            label={`Todos (${filterStats.all})`}
                            onClick={() => handleFilterClick('all')}
                            variant={selectedFilter === 'all' ? 'filled' : 'outlined'}
                            sx={{
                                backgroundColor: selectedFilter === 'all' ? 'primary.main' : 'transparent',
                                color: selectedFilter === 'all' ? 'white' : 'text.primary',
                                '&:hover': {
                                    backgroundColor: selectedFilter === 'all' ? 'primary.dark' : 'action.hover'
                                }
                            }}
                        />
                        {statusCategories.map(category => (
                            <Chip
                                key={category.key}
                                label={`${category.icon} ${category.label} (${filterStats[category.key]})`}
                                onClick={() => handleFilterClick(category.key)}
                                variant={selectedFilter === category.key ? 'filled' : 'outlined'}
                                sx={{
                                    backgroundColor: selectedFilter === category.key ? 'secondary.main' : 'transparent',
                                    color: selectedFilter === category.key ? 'white' : 'text.primary',
                                    '&:hover': {
                                        backgroundColor: selectedFilter === category.key ? 'secondary.dark' : 'action.hover'
                                    }
                                }}
                            />
                        ))}
                    </Box>
                </Box>

                {/* Filtros por fuente */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                        Tipo de Actividad:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {uniqueSources.map(source => (
                            <Chip
                                key={source}
                                label={`${getSourceIcon(source)} ${source} (${filterStats[source]})`}
                                onClick={() => handleFilterClick(source)}
                                variant={selectedFilter === source ? 'filled' : 'outlined'}
                                sx={{
                                    backgroundColor: selectedFilter === source ? 'info.main' : 'transparent',
                                    color: selectedFilter === source ? 'white' : 'text.primary',
                                    '&:hover': {
                                        backgroundColor: selectedFilter === source ? 'info.dark' : 'action.hover'
                                    }
                                }}
                            />
                        ))}
                    </Box>
                </Box>

                {/* ✅ Indicador de filtro activo */}
                {selectedFilter !== 'all' && (
                    <Box sx={{ 
                        p: 2, 
                        backgroundColor: 'info.light', 
                        borderRadius: 1, 
                        border: '1px solid',
                        borderColor: 'info.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <Typography variant="body2" sx={{ color: 'info.dark' }}>
                            🔍 Mostrando {filteredLogs.length} registros filtrados por: <strong>{selectedFilter}</strong>
                        </Typography>
                        <Button
                            size="small"
                            onClick={() => setSelectedFilter('all')}
                            sx={{ color: 'info.dark' }}
                        >
                            Limpiar filtro
                        </Button>
                    </Box>
                )}
            </Box>

            {/* Resumen de logs (actualizado con filtros) */}
            <Box sx={{ 
                mb: 4, 
                p: 3, 
                backgroundColor: '#f8f9fa', 
                borderRadius: 2,
                border: '1px solid #e0e0e0'
            }}>
                <Typography variant="h6" sx={{ 
                    mb: 2, 
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    📊 Resumen de Actividad {selectedFilter !== 'all' && `(Filtrado: ${selectedFilter})`}
                </Typography>
                
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ 
                                color: 'primary.main', 
                                fontWeight: 'bold' 
                            }}>
                                {filteredLogs.length}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {selectedFilter === 'all' ? 'Total Registros' : 'Registros Filtrados'}
                            </Typography>
                        </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ 
                                color: 'success.main', 
                                fontWeight: 'bold' 
                            }}>
                                {filteredLogs.filter(log => log.status >= 200 && log.status < 300).length}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Exitosos
                            </Typography>
                        </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ 
                                color: 'error.main', 
                                fontWeight: 'bold' 
                            }}>
                                {filteredLogs.filter(log => log.status >= 400).length}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Con Errores
                            </Typography>
                        </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ 
                                color: 'info.main', 
                                fontWeight: 'bold' 
                            }}>
                                {new Set(filteredLogs.map(log => log.source)).size}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Fuentes Únicas
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* Lista de logs filtrados */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {filteredLogs.length === 0 ? (
                    <Box sx={{ textAlign: 'center', p: 4 }}>
                        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                            🔍 No se encontraron registros
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            No hay registros que coincidan con el filtro seleccionado: <strong>{selectedFilter}</strong>
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={() => setSelectedFilter('all')}
                            sx={{ mt: 2 }}
                        >
                            Ver todos los registros
                        </Button>
                    </Box>
                ) : (
                    filteredLogs.map((log, index) => (
                        <Card key={log.id || index} sx={{ 
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.12)'
                            }
                        }}>
                            {/* Header del log */}
                            <Box sx={{ 
                                p: 2, 
                                backgroundColor: '#f8f9fa',
                                borderBottom: '1px solid #e0e0e0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                cursor: 'pointer'
                            }}
                            onClick={() => handleToggleExpand(log.id)}
                            >
                                {/* Avatar */}
                                {log.avatar && (
                                    <Box sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: '50%',
                                        overflow: 'hidden',
                                        border: '2px solid #e0e0e0'
                                    }}>
                                        <img
                                            src={log.avatar}
                                            alt={log.email}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'flex';
                                            }}
                                        />
                                        <Box sx={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'none',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#e0e0e0',
                                            color: '#666',
                                            fontSize: '1rem',
                                            fontWeight: 'bold'
                                        }}>
                                            {log.email?.charAt(0)?.toUpperCase() || '?'}
                                        </Box>
                                    </Box>
                                )}
                                
                                {/* Información principal */}
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                            {getSourceIcon(log.source)} {log.source}
                                        </Typography>
                                        <Chip
                                            label={log.status}
                                            size="small"
                                            sx={{
                                                ...getStatusColor(log.status),
                                                fontWeight: 'bold',
                                                fontSize: '0.75rem',
                                                minWidth: '60px'
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {log.email} • {new Date(log.date).toLocaleString('es-ES')}
                                    </Typography>
                                </Box>

                                {/* ID del log */}
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        ID: {log.id}
                                    </Typography>
                                </Box>

                                {/* Indicador de expansión */}
                                <IconButton size="small">
                                    {expandedLog === log.id ? '🔽' : '▶️'}
                                </IconButton>
                            </Box>

                            {/* Contenido expandido - resto del código permanece igual */}
                            {expandedLog === log.id && (
                                <CardContent sx={{ p: 3, backgroundColor: '#fafafa' }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="subtitle2" sx={{ 
                                                mb: 1, 
                                                fontWeight: 'bold',
                                                color: 'primary.main'
                                            }}>
                                                📧 Información del Usuario
                                            </Typography>
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                    <strong>Email:</strong> {log.email}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                    <strong>Fecha:</strong> {new Date(log.date).toLocaleString('es-ES')}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>Fuente:</strong> {log.source}
                                                </Typography>
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <Typography variant="subtitle2" sx={{ 
                                                mb: 1, 
                                                fontWeight: 'bold',
                                                color: 'secondary.main'
                                            }}>
                                                📊 Estado de la Respuesta
                                            </Typography>
                                            <Box sx={{ mb: 2 }}>
                                                <Chip
                                                    label={`HTTP ${log.status}`}
                                                    sx={{
                                                        ...getStatusColor(log.status),
                                                        fontWeight: 'bold',
                                                        fontSize: '0.875rem'
                                                    }}
                                                />
                                            </Box>
                                        </Grid>

                                        {log.body && (
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2" sx={{ 
                                                    mb: 1, 
                                                    fontWeight: 'bold',
                                                    color: 'info.main'
                                                }}>
                                                    📄 Contenido del Request
                                                </Typography>
                                                <Box sx={{
                                                    backgroundColor: '#fff',
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: 1,
                                                    p: 2,
                                                    maxHeight: '200px',
                                                    overflow: 'auto'
                                                }}>
                                                    <pre style={{
                                                        margin: 0,
                                                        fontSize: '0.875rem',
                                                        fontFamily: 'monospace',
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word'
                                                    }}>
                                                        {formatJsonBody(log.body)}
                                                    </pre>
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                </CardContent>
                            )}
                        </Card>
                    ))
                )}
            </Box>
        </Box>
    );
};

// Componente para mostrar las autorizaciones
const AuthorizationsTab = () => {
    const record = useRecordContext();

    if (!record || !record.authorization || record.authorization.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                    🔐 Autorizaciones del Usuario
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Este usuario no tiene autorizaciones asociadas.
                </Typography>
            </Box>
        );
    }

    const getServiceName = (serviceId) => {
        const services = {
            '1': 'Servicio Premium',
            '2': 'Servicio Básico',
            '3': 'Servicio Enterprise',
            '4': 'Servicio Trial'
        };
        return services[serviceId] || `Servicio ${serviceId}`;
    };

    const getServiceColor = (serviceId) => {
        const colors = {
            '1': { color: '#7b1fa2', backgroundColor: '#f3e5f5' }, // Premium - Púrpura
            '2': { color: '#1976d2', backgroundColor: '#e3f2fd' }, // Básico - Azul
            '3': { color: '#d84315', backgroundColor: '#ffebee' }, // Enterprise - Rojo
            '4': { color: '#388e3c', backgroundColor: '#e8f5e8' }  // Trial - Verde
        };
        return colors[serviceId] || { color: '#616161', backgroundColor: '#f5f5f5' };
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, color: 'primary.main' }}>
                🔐 Autorizaciones del Usuario ({record.authorization.length})
            </Typography>
            
            <Grid container spacing={3}>
                {record.authorization.map((auth, index) => (
                    <Grid item xs={12} sm={6} md={4} key={auth.id || index}>
                        <Card sx={{ 
                            height: '100%', 
                            display: 'flex', 
                            flexDirection: 'column',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 16px rgba(0,0,0,0.15)'
                            }
                        }}>
                            {/* Header de la tarjeta con avatar */}
                            <Box sx={{ 
                                p: 2, 
                                backgroundColor: '#f8f9fa',
                                borderBottom: '1px solid #e0e0e0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2
                            }}>
                                {auth.avatar && (
                                    <Box sx={{
                                        width: 50,
                                        height: 50,
                                        borderRadius: '50%',
                                        overflow: 'hidden',
                                        border: '2px solid #e0e0e0'
                                    }}>
                                        <img
                                            src={auth.avatar}
                                            alt={auth.username}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'flex';
                                            }}
                                        />
                                        <Box sx={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'none',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#e0e0e0',
                                            color: '#666',
                                            fontSize: '1.2rem',
                                            fontWeight: 'bold'
                                        }}>
                                            {auth.username?.charAt(0)?.toUpperCase() || '?'}
                                        </Box>
                                    </Box>
                                )}
                                
                                <Box sx={{ flex: 1 }}>
                                    {/* Nombre del usuario como enlace */}
                                    <Typography 
                                        component={Link} 
                                        to={`/users/${auth.id}/show`}
                                        variant="h6" 
                                        sx={{ 
                                            fontSize: '1rem',
                                            fontWeight: 'bold',
                                            color: 'primary.main',
                                            textDecoration: 'none',
                                            mb: 0.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            '&:hover': {
                                                textDecoration: 'underline',
                                                color: 'primary.dark'
                                            }
                                        }}
                                    >
                                        <PersonIcon fontSize="small" />
                                        {auth.username}
                                    </Typography>
                                    <Typography variant="body2" sx={{ 
                                        color: 'text.secondary',
                                        fontSize: '0.875rem'
                                    }}>
                                        ID: {auth.id}
                                    </Typography>
                                </Box>

                                {/* Botón adicional para ir al usuario */}
                                <Tooltip title="Ver perfil del usuario">
                                    <IconButton
                                        component={Link}
                                        to={`/users/${auth.id}/show`}
                                        size="small"
                                        sx={{
                                            backgroundColor: 'primary.main',
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: 'primary.dark'
                                            }
                                        }}
                                    >
                                        <PersonIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            {/* Contenido de la tarjeta */}
                            <CardContent sx={{ flexGrow: 1, p: 2 }}>
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" sx={{ 
                                        color: 'text.secondary', 
                                        mb: 1,
                                        fontWeight: 'bold'
                                    }}>
                                        📧 Email:
                                    </Typography>
                                    {/* Email también como enlace */}
                                    <Typography 
                                        component={Link} 
                                        to={`/users/${auth.id}`}
                                        variant="body2" 
                                        sx={{ 
                                            color: 'primary.main',
                                            textDecoration: 'none',
                                            mb: 2,
                                            wordBreak: 'break-word',
                                            '&:hover': {
                                                textDecoration: 'underline'
                                            }
                                        }}
                                    >
                                        {auth.email}
                                    </Typography>
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" sx={{ 
                                        color: 'text.secondary', 
                                        mb: 1,
                                        fontWeight: 'bold'
                                    }}>
                                        🛠️ Servicio Autorizado:
                                    </Typography>
                                    <Chip
                                        label={getServiceName(auth.servicios)}
                                        size="medium"
                                        sx={{
                                            ...getServiceColor(auth.servicios),
                                            fontWeight: 'bold',
                                            fontSize: '0.875rem',
                                            width: '100%',
                                            justifyContent: 'center'
                                        }}
                                    />
                                </Box>

                                {/* Información adicional */}
                                <Box sx={{ 
                                    mt: 2, 
                                    pt: 2, 
                                    borderTop: '1px solid #e0e0e0',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <Typography variant="caption" sx={{ 
                                        color: 'text.secondary',
                                        fontStyle: 'italic'
                                    }}>
                                        Autorización activa
                                    </Typography>
                                    <Box sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: '#4caf50'
                                    }} />
                                </Box>

                                {/* Botón de acción adicional */}
                                <Box sx={{ mt: 2 }}>
                                    <Button
                                        component={Link}
                                        to={`/users/${auth.id}/show`}
                                        variant="outlined"
                                        size="small"
                                        startIcon={<PersonIcon />}
                                        fullWidth
                                        sx={{
                                            borderColor: 'primary.main',
                                            color: 'primary.main',
                                            '&:hover': {
                                                backgroundColor: 'primary.main',
                                                color: 'white'
                                            }
                                        }}
                                    >
                                        Ver Usuario Completo
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Resumen de autorizaciones - sin cambios */}
            <Box sx={{ 
                mt: 4, 
                p: 3, 
                backgroundColor: '#f8f9fa', 
                borderRadius: 2,
                border: '1px solid #e0e0e0'
            }}>
                <Typography variant="h6" sx={{ 
                    mb: 2, 
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    📊 Resumen de Autorizaciones
                </Typography>
                
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ 
                                color: 'primary.main', 
                                fontWeight: 'bold' 
                            }}>
                                {record.authorization.length}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Total Autorizaciones
                            </Typography>
                        </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ 
                                color: 'success.main', 
                                fontWeight: 'bold' 
                            }}>
                                {record.authorization.length}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Activas
                            </Typography>
                        </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ 
                                color: 'info.main', 
                                fontWeight: 'bold' 
                            }}>
                                {new Set(record.authorization.map(auth => auth.servicios)).size}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Servicios Únicos
                            </Typography>
                        </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ 
                                color: 'warning.main', 
                                fontWeight: 'bold' 
                            }}>
                                {record.authorization.filter(auth => auth.avatar).length}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Con Avatar
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

// Componente para mostrar los proyectos
const ProjectsTab = () => {
    const record = useRecordContext();
    const [selectedImage, setSelectedImage] = useState(null);
    const [imageDialogOpen, setImageDialogOpen] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0); // New state for image navigation

    if (!record || !record.projects || record.projects.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                    📁 Proyectos del Usuario
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Este usuario no tiene proyectos asociados.
                </Typography>
            </Box>
        );
    }

    // Function to parse images from comma-separated string
    const parseImages = (imagesString) => {
        if (!imagesString) return [];
        return imagesString.split(',').map(img => img.trim()).filter(img => img.length > 0);
    };

    const handleImageClick = (imageUrl, projectName, imageIndex = 0, allImages = []) => {
        setSelectedImage({ 
            url: imageUrl, 
            name: projectName, 
            allImages: allImages,
            currentIndex: imageIndex 
        });
        setCurrentImageIndex(imageIndex);
        setImageError(false);
        setImageDialogOpen(true);
    };

    const handleImageError = () => {
        setImageError(true);
    };

    // Navigation functions for multiple images
    const handlePreviousImage = () => {
        if (selectedImage && selectedImage.allImages.length > 1) {
            const newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : selectedImage.allImages.length - 1;
            setCurrentImageIndex(newIndex);
            setSelectedImage({
                ...selectedImage,
                url: selectedImage.allImages[newIndex],
                currentIndex: newIndex
            });
            setImageError(false);
        }
    };

    const handleNextImage = () => {
        if (selectedImage && selectedImage.allImages.length > 1) {
            const newIndex = currentImageIndex < selectedImage.allImages.length - 1 ? currentImageIndex + 1 : 0;
            setCurrentImageIndex(newIndex);
            setSelectedImage({
                ...selectedImage,
                url: selectedImage.allImages[newIndex],
                currentIndex: newIndex
            });
            setImageError(false);
        }
    };

    // Function to download images with multiple methods
    const handleDownloadWithCanvas = async (imageUrl, fileName) => {
        try {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            
            img.onload = function() {
                try {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    
                    canvas.width = img.width;
                    canvas.height = img.height;
                    
                    ctx.drawImage(img, 0, 0);
                    
                    canvas.toBlob(function(blob) {
                        if (blob) {
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = fileName || 'project-image.png';
                            link.style.display = 'none';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                        } else {
                            handleDownloadFallback(imageUrl, fileName);
                        }
                    }, 'image/png');
                } catch (canvasError) {
                    console.error('Canvas error:', canvasError);
                    handleDownloadFallback(imageUrl, fileName);
                }
            };
            
            img.onerror = function() {
                console.log('Image load error, trying fallback method');
                handleDownloadFallback(imageUrl, fileName);
            };
            
            img.src = imageUrl;
            
        } catch (error) {
            console.error('Canvas download failed:', error);
            handleDownloadFallback(imageUrl, fileName);
        }
    };

    // Fallback download method
    const handleDownloadFallback = async (imageUrl, fileName) => {
        try {
            const response = await fetch(imageUrl, {
                method: 'GET',
                mode: 'cors',
            });
            
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName || 'project-image.png';
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                throw new Error('Fetch failed');
            }
        } catch (error) {
            console.error('All download methods failed:', error);
            window.open(imageUrl, '_blank', 'noopener,noreferrer');
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'created':
                return { color: '#2e7d32', backgroundColor: '#e8f5e8' };
            case 'in progress':
                return { color: '#1976d2', backgroundColor: '#e3f2fd' };
            case 'completed':
                return { color: '#7b1fa2', backgroundColor: '#f3e5f5' };
            default:
                return { color: '#616161', backgroundColor: '#f5f5f5' };
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, color: 'primary.main' }}>
                📁 Proyectos del Usuario ({record.projects.length})
            </Typography>
            
            <Grid container spacing={3}>
                {record.projects.map((project) => {
                    const projectImages = parseImages(project.images);
                    const mainImage = projectImages[0] || project.images; // Fallback to original if parsing fails
                    
                    return (
                        <Grid item xs={12} sm={6} md={4} key={project.id}>
                            <Card sx={{ 
                                height: '100%', 
                                display: 'flex', 
                                flexDirection: 'column',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 16px rgba(0,0,0,0.15)'
                                }
                            }}>
                                {/* Main image with overlay indicators */}
                                <Box sx={{ position: 'relative' }}>
                                    <CardMedia
                                        component="img"
                                        height="200"
                                        image={mainImage}
                                        alt={project.nombre}
                                        sx={{ 
                                            cursor: 'pointer',
                                            objectFit: 'cover'
                                        }}
                                        onClick={() => handleImageClick(mainImage, project.nombre, 0, projectImages)}
                                    />
                                    
                                    {/* Multiple images indicator */}
                                    {projectImages.length > 1 && (
                                        <Box sx={{
                                            position: 'absolute',
                                            top: 8,
                                            left: 8,
                                            backgroundColor: 'rgba(0,0,0,0.7)',
                                            color: 'white',
                                            padding: '4px 8px',
                                            borderRadius: '12px',
                                            fontSize: '0.75rem',
                                            fontWeight: 'bold',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5
                                        }}>
                                            📷 {projectImages.length}
                                        </Box>
                                    )}
                                    
                                    {/* Action buttons */}
                                    <Box sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        display: 'flex',
                                        gap: 1
                                    }}>
                                        <Tooltip title="Ver imágenes">
                                            <IconButton
                                                size="small"
                                                sx={{ 
                                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                                    '&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
                                                }}
                                                onClick={() => handleImageClick(mainImage, project.nombre, 0, projectImages)}
                                            >
                                                <ZoomInIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        
                                        <Tooltip title="Descargar imagen principal">
                                            <IconButton
                                                size="small"
                                                sx={{ 
                                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                                    '&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
                                                }}
                                                onClick={() => handleDownloadWithCanvas(
                                                    mainImage, 
                                                    `${project.nombre.replace(/\s+/g, '_')}_${project.id}_1.png`
                                                )}
                                            >
                                                <DownloadIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>

                                    {/* Project status */}
                                    <Box sx={{
                                        position: 'absolute',
                                        bottom: 8,
                                        left: 8
                                    }}>
                                        <Chip
                                            label={project.status}
                                            size="small"
                                            sx={{
                                                ...getStatusColor(project.status),
                                                fontWeight: 'bold',
                                                fontSize: '0.75rem'
                                            }}
                                        />
                                    </Box>
                                </Box>

                                {/* Image thumbnails for multiple images */}
                                {projectImages.length > 1 && (
                                    <Box sx={{ 
                                        p: 1, 
                                        backgroundColor: '#f8f9fa',
                                        borderBottom: '1px solid #e0e0e0'
                                    }}>
                                        <Box sx={{ 
                                            display: 'flex', 
                                            gap: 1, 
                                            overflowX: 'auto',
                                            '&::-webkit-scrollbar': {
                                                height: '4px',
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                backgroundColor: '#f1f1f1',
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                backgroundColor: '#888',
                                                borderRadius: '2px',
                                            }
                                        }}>
                                            {projectImages.map((image, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        minWidth: '40px',
                                                        height: '40px',
                                                        borderRadius: '4px',
                                                        overflow: 'hidden',
                                                        cursor: 'pointer',
                                                        border: index === 0 ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                                        '&:hover': {
                                                            border: '2px solid #1976d2'
                                                        }
                                                    }}
                                                    onClick={() => handleImageClick(image, project.nombre, index, projectImages)}
                                                >
                                                    <img
                                                        src={image}
                                                        alt={`${project.nombre} - ${index + 1}`}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}

                                {/* Card content */}
                                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                                    <Typography variant="h6" sx={{ 
                                        mb: 1, 
                                        fontSize: '1rem',
                                        fontWeight: 'bold',
                                        color: 'text.primary'
                                    }}>
                                        {project.nombre}
                                    </Typography>
                                    
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                            <strong>ID:</strong> {project.id}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                            <strong>Escala:</strong> 1:{project.escala}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                            <strong>Fecha:</strong> {new Date(project.fecha).toLocaleDateString('es-ES')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                            <strong>Imágenes:</strong> {projectImages.length}
                                        </Typography>
                                        {project.alias && (
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                <strong>Alias:</strong> {project.alias}
                                            </Typography>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Enhanced Dialog for image viewing with navigation */}
            <Dialog
                open={imageDialogOpen}
                onClose={() => setImageDialogOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { backgroundColor: 'rgba(0,0,0,0.9)' }
                }}
            >
                <DialogActions sx={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}>
                    <IconButton
                        onClick={() => setImageDialogOpen(false)}
                        sx={{ color: 'white' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogActions>
                
                <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', position: 'relative' }}>
                    {selectedImage && (
                        <Box sx={{ textAlign: 'center', width: '100%', position: 'relative' }}>
                            {/* Navigation arrows for multiple images */}
                            {selectedImage.allImages && selectedImage.allImages.length > 1 && (
                                <>
                                    <IconButton
                                        onClick={handlePreviousImage}
                                        sx={{
                                            position: 'absolute',
                                            left: 16,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            backgroundColor: 'rgba(255,255,255,0.1)',
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255,255,255,0.2)'
                                            },
                                            zIndex: 2
                                        }}
                                    >
                                        ◀️
                                    </IconButton>
                                    
                                    <IconButton
                                        onClick={handleNextImage}
                                        sx={{
                                            position: 'absolute',
                                            right: 16,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            backgroundColor: 'rgba(255,255,255,0.1)',
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255,255,255,0.2)'
                                            },
                                            zIndex: 2
                                        }}
                                    >
                                        ▶️
                                    </IconButton>
                                </>
                            )}

                            {!imageError ? (
                                <img
                                    src={selectedImage.url}
                                    alt={selectedImage.name}
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '80vh',
                                        objectFit: 'contain'
                                    }}
                                    onError={handleImageError}
                                />
                            ) : (
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    minHeight: '300px',
                                    color: 'white'
                                }}>
                                    <Typography variant="h6" sx={{ mb: 2 }}>
                                        No se pudo cargar la imagen
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        onClick={() => window.open(selectedImage.url, '_blank')}
                                        sx={{ mb: 2 }}
                                    >
                                        Abrir imagen en nueva pestaña
                                    </Button>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                        URL: {selectedImage.url}
                                    </Typography>
                                </Box>
                            )}
                            
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="h6" sx={{ color: 'white' }}>
                                    {selectedImage.name}
                                </Typography>
                                {selectedImage.allImages && selectedImage.allImages.length > 1 && (
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                        Imagen {currentImageIndex + 1} de {selectedImage.allImages.length}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                
                <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={() => selectedImage && handleDownloadWithCanvas(
                            selectedImage.url,
                            `${selectedImage.name.replace(/\s+/g, '_')}_${currentImageIndex + 1}.png`
                        )}
                        sx={{ backgroundColor: 'primary.main' }}
                    >
                        Descargar Imagen Actual
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

// Componente para mostrar estado de cancelación en la lista
const CancellationStatusField = () => {
    const record = useRecordContext();
    
    if (!record) {
        return <span>-</span>;
    }
    
    // Solo mostrar para usuarios de visualscaleVR
    if (record.source !== 'visualscaleVR') {
        return <span>-</span>;
    }
    
    // Debug para ver todos los campos disponibles
    console.log('🔍 Available fields in record:', Object.keys(record));
    console.log('🔍 Full record:', record);
    
    // Buscar campos relacionados con oculus o cancelación
    const oculusRelatedFields = Object.keys(record).filter(key => 
        key.toLowerCase().includes('oculus') || 
        key.toLowerCase().includes('cancel') ||
        key.toLowerCase().includes('subscription')
    );
    
    console.log('🔍 Oculus/cancellation related fields:', oculusRelatedFields);
    
    // Verificar si hay suscripciones de Oculus canceladas
    let hasCancellation = false;
    let cancelledCount = 0;
    
    // Verificar diferentes posibles ubicaciones de los datos de oculus
    const possibleOculusFields = [record.oculus, record.Oculus, record.OCULUS];
    let oculusData = null;
    
    for (const field of possibleOculusFields) {
        if (field && Array.isArray(field)) {
            oculusData = field;
            console.log('🔍 Found oculus data in:', field);
            break;
        }
    }
    
    if (oculusData && Array.isArray(oculusData)) {
        console.log('🔍 Oculus data found with length:', oculusData.length);
        
        for (let i = 0; i < oculusData.length; i++) {
            const sub = oculusData[i];
            console.log(`🔍 Checking oculus subscription ${i}:`, sub);
            
            if (sub.cancellation_time && sub.cancellation_time !== null && sub.cancellation_time !== '') {
                console.log('🔍 FOUND CANCELLATION!', sub.cancellation_time);
                hasCancellation = true;
                cancelledCount++;
            }
        }
    } else {
        console.log('🔍 No oculus data found in any expected field');
    }
    
    // Verificar cancelación principal
    if (record.subscriptioncancellationtime && 
        record.subscriptioncancellationtime !== null && 
        record.subscriptioncancellationtime !== '') {
        hasCancellation = true;
        console.log('🔍 Found main cancellation:', record.subscriptioncancellationtime);
    }
    
    console.log('🔍 Final check:', {
        username: record.username,
        hasCancellation,
        cancelledCount
    });
    
    // Si no hay datos de oculus, mostrar un indicador especial
    if (!oculusData) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span style={{ fontSize: '0.875rem' }}>❓</span>
                <Typography variant="body2" sx={{ 
                    color: '#666', 
                    fontWeight: 'normal',
                    fontSize: '0.875rem'
                }}>
                    Sin datos Oculus
                </Typography>
            </Box>
        );
    }
    
    // Retornar basado en el estado
    if (hasCancellation) {
        console.log('🔍 Returning CANCELLED status');
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span style={{ fontSize: '0.875rem' }}>❌</span>
                <Typography variant="body2" sx={{ 
                    color: '#d32f2f', 
                    fontWeight: 'bold',
                    fontSize: '0.875rem'
                }}>
                    Cancelada
                </Typography>
                {cancelledCount > 0 && (
                    <Typography variant="caption" sx={{ 
                        color: '#666',
                        fontSize: '0.75rem',
                        ml: 0.5
                    }}>
                        ({cancelledCount} Oculus)
                    </Typography>
                )}
            </Box>
        );
    } else {
        console.log('🔍 Returning ACTIVE status');
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span style={{ fontSize: '0.875rem' }}>✅</span>
                <Typography variant="body2" sx={{ 
                    color: '#2e7d32', 
                    fontWeight: 'bold',
                    fontSize: '0.875rem'
                }}>
                    Activa
                </Typography>
            </Box>
        );
    }
};

const UserFilters = [
    <TextInput source="q" label="Buscar" alwaysOn />,
    <TextInput source="username" label="User Name" />,
    <TextInput source="email" label="Email" />,
    <TextField source="oculusid" label="Oculus ID" />,
    <TextInput source="tokenvr" label="Token" />,
    <TextInput source="source" label="Source" />,
    
    <SelectInput 
        source="tipotrial" 
        label="Tipo de Licencia" 
        choices={[
            { id: 'pago', name: 'PAGO' },
            { id: 'FREE_TRIAL', name: 'FREE_TRIAL' },
            { id: 'INTRO_OFFER', name: 'INTRO_OFFER' }
        ]}
        parse={value => value === 'pago' ? ' ' : value}
        allowEmpty
    />,

    // ✅ Nuevos filtros para el estado de suscripción/expiración
    <SelectInput 
        source="subscription_status" 
        label="Estado de Suscripción" 
        choices={[
            { id: 'active', name: 'Activa' },
            { id: 'expired', name: 'Expirada' },
            { id: 'expires_today', name: 'Expira Hoy' },
            { id: 'cancelled', name: 'Cancelada' },
            { id: 'cancelled_active', name: 'Cancelada pero Activa' }
        ]}
        allowEmpty
    />,
    
    // ✅ Filtros por fechas de suscripción
    <DateInput 
        source="subscriptionstarttime_gte" 
        label="Suscripción desde" 
    />,
    <DateInput 
        source="subscriptionstarttime_lte" 
        label="Suscripción hasta" 
    />,
    <DateInput 
        source="subscriptionendtime_gte" 
        label="Expira desde" 
    />,
    <DateInput 
        source="subscriptionendtime_lte" 
        label="Expira hasta" 
    />,
    
    // ✅ Filtro para días restantes
    <SelectInput 
        source="days_remaining" 
        label="Días Restantes" 
        choices={[
            { id: 'expired', name: 'Ya Expirada' },
            { id: '0-7', name: 'Menos de 7 días' },
            { id: '7-30', name: '7-30 días' },
            { id: '30-90', name: '30-90 días' },
            { id: '90+', name: 'Más de 90 días' }
        ]}
        allowEmpty
    />
];

const UserListActions = () => (
    <TopToolbar>
        <FilterButton />
        <CreateButton />
        <ExportButton />
    </TopToolbar>
);

export const UserList = () => (
    <List
        filters={UserFilters}
        actions={<UserListActions />}
        perPage={50}
        sort={{ field: 'date', order: 'DESC' }}
        // ✅ Agregamos filterDefaultValues para mostrar filtros útiles por defecto
        filterDefaultValues={{}}
    >
        <Datagrid bulkActionButtons={<CustomBulkDeleteButton />}>
            <TextField source="id" />
            <CustomerAvatar size="small" />
            <TextField source="username" label="User Name" />
            <EmailField source="email" label="Email" />
            <TextField source="source" label="Source" />
            <TextField source="oculusid" label="Oculus ID" />
            <TextField source="projects" label="# Projects" />
            <TextField source="tipotrial" label="Licencia" />
            <TextField source="currentprice" label="Precio" />
            <SubscriptionDurationField label="Expiration" />
            <CancellationStatusField label="Estado Cancelación" />
            <TextField source="cliente" label="cliente" />
            <TextField source="tokenvr" label="Token" />
            <TextField source="nivel" label="Role" />
            <DateField source="date" label="Registration Date" />
            <DateField source="subscriptionstarttime" label="Subscription Start" />
            <DateField source="subscriptionendtime" label="Subscription End" />
            <DateField source="lastaccessweb" label="Last Web Date" />
            <DateField source="lastaccessvr" label="Last VR Date" />
            <TextField source="GafasOWeb" label="Gafas o Web" />
            <CustomDeleteButton />
        </Datagrid>
    </List>
);

// Componente para editar usuarios

export const UserEdit = () => (
    <Edit>
        <SimpleForm>
            <TextField source="username" label="User Name" />
            <EmailField source="email" label="Email" />
            <TextField source="source" label="Source" />
            <TextField source="tipotrial" label="Licencia" />
            <TextField source="cliente" label="cliente" />
            <TextField source="tokenvr" label="Token" />
            <TextField source="nivel" label="Role" />
            <TextField source="date" label="Registration Date" />
            <TextField source="lastaccessweb" label="Last Web Date" />
            <TextField source="lastaccessvr" label="Last VR Date" />
            <TextField source="GafasOWeb" label="Gafas o Web" />
        </SimpleForm>
    </Edit>
);

// Componente para crear usuarios
export const UserCreate = () => (
    <Create>
        <SimpleForm>
            <TextField source="username" label="User Name" />
            <EmailField source="email" label="Email" />
            <TextField source="cliente" label="Cliente" />
            <TextField source="tokenvr" label="Token" />
            <TextField source="source" label="Source" />
            <TextField source="tipotrial" label="Licencia" />
            <TextField source="nivel" label="Role" />
            <TextField source="date" label="Registration Date" />
            <TextField source="lastaccessweb" label="Last Web Date" />
            <TextField source="lastaccessvr" label="Last VR Date" />
            <TextField source="GafasOWeb" label="Gafas o Web" />
        </SimpleForm>
    </Create>
);


export const UserShow = () => {
    // Usar useShowController en lugar de useRecordContext para obtener los datos
    const { record, isLoading, error } = useShowController();
    
    // Debug más detallado del estado del record
    console.log('🔍 Debug - useShowController result:', { record, isLoading, error });
    console.log('🔍 Debug - Record:', record);
    console.log('🔍 Debug - isLoading:', isLoading);
    console.log('🔍 Debug - error:', error);
    
    // Mostrar loading mientras se cargan los datos
    if (isLoading) {
        console.log('🔍 Debug - Showing loading state (isLoading = true)');
        return (
            <Show>
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography>Cargando datos del usuario...</Typography>
                </Box>
            </Show>
        );
    }
    
    // Mostrar error si hay algún problema
    if (error) {
        console.log('🔍 Debug - Error loading record:', error);
        return (
            <Show>
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: 'error.main', mb: 2 }}>
                        ❌ Error al cargar los datos
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {error.message || 'Error desconocido'}
                    </Typography>
                </Box>
            </Show>
        );
    }
    
    // Si no hay record después de cargar
    if (!record) {
        console.log('🔍 Debug - No record found after loading');
        return (
            <Show>
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: 'warning.main', mb: 2 }}>
                        ⚠️ No se encontraron datos del usuario
                    </Typography>
                </Box>
            </Show>
        );
    }
    
    // Debug: Verificar qué valor exacto viene en source
    console.log('🔍 Debug - Record completo:', record);
    console.log('🔍 Debug - Source value:', record?.source);
    console.log('🔍 Debug - Source type:', typeof record?.source);
    console.log('🔍 Debug - Source length:', record?.source?.length);
    
    // Verificar si el origen es VisualscaleVR para ocultar ciertas tabs
    const sourceValue = record?.source?.toLowerCase()?.trim();
    const isVisualscaleVR = sourceValue === 'visualscalevr' || 
        sourceValue === 'visualscale vr' ||
        sourceValue === 'visualscale_vr' ||
        sourceValue === 'visualscale-vr';
    
    console.log('🔍 Debug - Source normalizado:', sourceValue);
    console.log('🔍 Debug - isVisualscaleVR:', isVisualscaleVR);
    
    return (
        <Show
            actions={
                <TopToolbar>
                    <BackButton /> 
                    <DeleteFromShowButton />
                </TopToolbar>
            }>
            <TabbedShowLayout>
                <TabbedShowLayout.Tab label="Summary">
                    <Box sx={{ padding: 3 }}>
                        {/* Debug info visible en la UI 
                        <Box sx={{ 
                            mb: 2, 
                            p: 2, 
                            backgroundColor: '#fff3cd', 
                            border: '1px solid #ffeaa7',
                            borderRadius: 1 
                        }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                🔍 DEBUG INFO:

                                Record state: LOADED

                                Record keys: {Object.keys(record).join(', ')}

                                Source original: "{record?.source}"

                                Source normalizado: "{sourceValue}"

                                isVisualscaleVR: {isVisualscaleVR ? 'TRUE' : 'FALSE'}
                            </Typography>
                        </Box>*/}

                        {/* Header con Avatar y Info Principal */}
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'flex-start', 
                            mb: 4,
                            p: 3,
                            backgroundColor: '#f8f9fa',
                            borderRadius: 2,
                            border: '1px solid #e0e0e0'
                        }}>
                            <CustomerAvatar size="large" />
                            <Box sx={{ ml: 3, flex: 1 }}>
                                <SimpleShowLayout>
                                    <TextField source="username" label="Nombre de Usuario" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }} />
                                    <EmailField source="email" label="Email" />
                                    {isVisualscaleVR && <TextField source="cliente" label="Cliente" />}
                                    <TextField source="nivel" label="Role" />
                                </SimpleShowLayout>
                            </Box>
                            <Box sx={{ ml: 2, minWidth: '300px' }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                                    Estado de Suscripción
                                </Typography>
                                <SubscriptionDurationField />
                            </Box>
                        </Box>

                        {/* Grid de Información Organizada */}
                        <Box sx={{ 
                            display: 'grid', 
                            gridTemplateColumns: { xs: '1fr', md: !isVisualscaleVR ? '1fr' : '1fr 1fr' }, 
                            gap: 3,
                            mb: 3 
                        }}>
                            {/* Información de Suscripción */}
                            {isVisualscaleVR && (
                                <Box sx={{ 
                                    p: 3, 
                                    backgroundColor: '#fff', 
                                    borderRadius: 2, 
                                    border: '1px solid #e0e0e0',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}>
                                    <Typography variant="h6" sx={{ 
                                        mb: 2, 
                                        color: 'primary.main',
                                        borderBottom: '2px solid',
                                        borderColor: 'primary.main',
                                        pb: 1
                                    }}>
                                        📅 Información de Suscripción
                                    </Typography>
                                    <SimpleShowLayout>
                                        <DateField source="subscriptionstarttime" label="Inicio de Suscripción" />
                                        <DateField source="subscriptionendtime" label="Fin de Suscripción" />
                                        <DateField source="subscriptioncancellationtime" label="Fecha de Cancelación" />
                                        <TextField source="tipotrial" label="Tipo de Licencia" />
                                        <TextField source="currentprice" label="Precio" />
                                    </SimpleShowLayout>
                                </Box>
                            )}

                            {/* Información Técnica */}
                            <Box sx={{ 
                                p: 3, 
                                backgroundColor: '#fff', 
                                borderRadius: 2, 
                                border: '1px solid #e0e0e0',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}>
                                <Typography variant="h6" sx={{ 
                                    mb: 2, 
                                    color: 'secondary.main',
                                    borderBottom: '2px solid',
                                    borderColor: 'secondary.main',
                                    pb: 1
                                }}>
                                    🔧 Información Técnica
                                </Typography>
                                <SimpleShowLayout>
                                    <TextField source="tokenvr" label="Token VR" />
                                    <TextField source="GafasOWeb" label="Dispositivo de Acceso" />
                                    <TextField source="source" label="Origen" />
                                    <TextField source="oculusid" label="Oculus ID" />
                                </SimpleShowLayout>
                            </Box>
                        </Box>

                        {/* Actividad Reciente - Ancho completo */}
                        <Box sx={{ 
                            p: 3, 
                            backgroundColor: '#fff', 
                            borderRadius: 2, 
                            border: '1px solid #e0e0e0',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                            <Typography variant="h6" sx={{ 
                                mb: 2, 
                                color: 'success.main',
                                borderBottom: '2px solid',
                                borderColor: 'success.main',
                                pb: 1
                            }}>
                                📊 Actividad Reciente
                            </Typography>
                            <Box sx={{ 
                                display: 'grid', 
                                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, 
                                gap: 2 
                            }}>
                                <SimpleShowLayout>
                                    <DateField source="date" label="Fecha de Registro" />
                                </SimpleShowLayout>
                                <SimpleShowLayout>
                                    <DateField source="lastaccessweb" label="Último Acceso Web" />
                                </SimpleShowLayout>
                                <SimpleShowLayout>
                                    <DateField source="lastaccessvr" label="Último Acceso VR" />
                                </SimpleShowLayout>
                            </Box>
                        </Box>
                    </Box>
                </TabbedShowLayout.Tab>

                {/* Condicionalmente mostrar la tab de Projects solo si NO es VisualscaleVR */}
                {!isVisualscaleVR && (
                    <TabbedShowLayout.Tab label={`Projects (${record?.projects?.length || 0})`}>
                        <ProjectsTab />
                    </TabbedShowLayout.Tab>                    
                )}

                {/* Condicionalmente mostrar la tab de Authorizations solo si NO es VisualscaleVR */}
                {!isVisualscaleVR && (
                    <TabbedShowLayout.Tab label="Authorizations">
                        <AuthorizationsTab />
                    </TabbedShowLayout.Tab>
                )}

                <TabbedShowLayout.Tab label="Logs">
                    <LogsTab />
                </TabbedShowLayout.Tab>

                {isVisualscaleVR && (
                  <TabbedShowLayout.Tab label="License History">
                      <LicenseHistoryTab />
                  </TabbedShowLayout.Tab>
                )}

                {isVisualscaleVR && (
                    <TabbedShowLayout.Tab label="Oculus Subscriptions">
                        <OculusSubscriptions />
                    </TabbedShowLayout.Tab>
                )}
                <TabbedShowLayout.Tab label="AI">
                    <AITab />
                </TabbedShowLayout.Tab>

            </TabbedShowLayout>
        </Show>
    );
};